'use client'

import { productTemplateService } from '@/domain/ProductTemplate'
import sleep from '@/utils/sleep'
import { LoadingOverlay } from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import React from 'react'
import { toast } from 'react-toastify'
import CustomerFavorite from 'stoux-shared-types/models/customer-favorite'
import { useAccountContext } from './account-context'

interface FavoriteContext {
    addFavorite: (templateId: string) => void
    removeFavorite: (templateId: string) => void
    getIsFavorite: (templateId: string) => boolean
    favorites: CustomerFavorite[]
    count: number
}

const FavoriteContext = React.createContext<FavoriteContext | null>(null)

export const useFavorites = () => {
    const context = React.useContext(FavoriteContext)
    if (context === null) {
        throw new Error('useFavorites must be used within a FavoriteProvider')
    }
    return context
}

interface FavoriteProps {
    children: React.ReactNode
}

const IS_SERVER = typeof window === 'undefined'
const FAKE_UI_DELAY = 300 // ms

/**
 * We create a favorite from the template id
 */
function useCreateFavorite() {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: (templateId: string) =>
            productTemplateService.createFavorite(templateId),

        onMutate: async (templateId) => {
            await sleep(FAKE_UI_DELAY) // For UI feedback
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'favorites',
            })
        },

        onError: () => {
            toast.error("Erreur lors de l'ajout aux favoris")
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'favorites',
            })
        },
    })

    return mutation
}

/**
 * We delete the favorite not from the template id but from the favorite id directly
 */
function useDeleteFavorite() {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: (favoriteId: string) =>
            productTemplateService.deleteFavorite(favoriteId),

        onMutate: async (templateId) => {
            await sleep(FAKE_UI_DELAY) // For UI feedback
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'favorites',
            })
        },

        onError: () => {
            toast.error('Erreur lors de la suppression des favoris')
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'favorites',
            })
        },
    })

    return mutation
}

export function useListFavorites() {
    const { customer } = useAccountContext()
    const query = useQuery({
        queryKey: ['favorites', customer?.id],
        queryFn: () => productTemplateService.listFavorites(),
        enabled: !!customer?.id,
    })

    return {
        ...query,
        favorites: query.data?.favorites || [],
        count: query.data?.count || 0,
    }
}

export const FavoriteProvider = ({ children }: FavoriteProps) => {
    const { customer } = useAccountContext()
    const router = useRouter()

    const createFavorite = useCreateFavorite()
    const deleteFavorite = useDeleteFavorite()

    const isMutating = createFavorite.isPending || deleteFavorite.isPending

    const { favorites, count } = useListFavorites()

    const addFavorite = async (templateId: string) => {
        if (!customer || !customer.id) {
            const { asPath } = router
            router.push(`/sign-in?redirect=${asPath}`)
            return
        }
        await createFavorite.mutateAsync(templateId)
    }

    const removeFavorite = async (templateId: string) => {
        if (!customer || !customer.id) {
            const { asPath } = router
            router.push(`/sign-in?redirect=${asPath}`)
            return
        }

        const foundFavorite = favorites?.find(
            (favorite) => favorite.template.id === templateId,
        )

        if (!foundFavorite) return
        await deleteFavorite.mutateAsync(foundFavorite?.id)
    }

    const getIsFavorite = (templateId: string) => {
        if (!favorites || !favorites?.length) return false

        return favorites?.some(
            (favorite) => favorite.template.id === templateId,
        )
    }

    return (
        <FavoriteContext.Provider
            value={{
                favorites: favorites || [],
                count: count || 0,
                addFavorite,
                removeFavorite,
                getIsFavorite,
            }}
        >
            {children}
        </FavoriteContext.Provider>
    )
}
